import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import { 
  ArrowUpFromLine, 
  Package, 
  Truck, 
  CheckCircle2, 
  Search,
  Clock,
  User, 
  Globe, 
  ShoppingBag,
  AlertCircle,
  Loader2,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Boxes
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { isBestellung as isBestellungShared } from '@/lib/orderFields';
import { cn } from '@/lib/utils';
import { parseArrayField } from '@/lib/jsonField';
import { thumb, IMG_PROPS } from '@/lib/imageUrl';
import { toast } from 'sonner';
import {
  commitStock,
  generateDocNumber,
  StockApiError,
  stockErrorToGerman,
} from '@/lib/stockApi';
import { useAuth } from '@/api/AuthContext';
import { downloadWarenausgangPaket } from '@/components/orders/WarenausgangPaketPDF';
import { downloadLieferscheinBeideExemplare } from '@/components/orders/LieferscheinPDF';
import { downloadRechnung } from '@/components/orders/RechnungPDF';
import OrderDocuments from '@/components/orders/OrderDocuments';
import { completeLieferungTasksForLead } from '@/components/tasks/TaskStateMachine';
import { pushStockForProducts } from '@/lib/shopifyPush';
import { pushOrderInBackground } from '@/lib/shopifyOrderPush';
import { ensureInvoice } from '@/lib/rechnungVersand';
import { useEigeneZustellung } from '@/lib/useEigeneZustellung';
import { istEigeneZustellung } from '@/lib/versandArt';
import StatusBadge from '@/components/ui/StatusBadge';

/**
 * PositionenList — sipariş kalemleri. Mobilde ilk 3 kalem gösterilir,
 * kalanı "N weitere anzeigen" ile açılır (uzun listeler ekranı yutmasın).
 * Masaüstünde (sm+) tüm liste her zaman açık.
 */
const MOBILE_VISIBLE_ITEMS = 3;

function PositionenList({ positionen, products }) {
  const [expanded, setExpanded] = useState(false);
  const hiddenCount = positionen.length - MOBILE_VISIBLE_ITEMS;

  return (
    <div className="space-y-2">
      {positionen.map((item, idx) => {
        const product = products.find(p => p.id === item.product_id);
        const isUnmatched = !item.product_id;
        const isLowStock = !isUnmatched && (product?.stock || 0) < (item.quantity || 1);
        const hiddenOnMobile = !expanded && idx >= MOBILE_VISIBLE_ITEMS;

        return (
          <div
            key={idx}
            className={cn(
              'flex items-center justify-between gap-2 py-2 px-3 bg-white border rounded-lg group',
              isUnmatched ? 'border-red-200 bg-red-50/50' : 'border-slate-100',
              hiddenOnMobile && 'hidden sm:flex'
            )}
          >
            <div className="flex items-center gap-2.5 min-w-0">
              <span className="w-7 h-7 flex-shrink-0 rounded bg-slate-100 text-[10px] font-bold flex items-center justify-center group-hover:bg-slate-900 group-hover:text-white transition-colors">
                {item.quantity}x
              </span>
              {product?.image_url && (
                <img {...IMG_PROPS} loading="lazy" width={28} height={28} src={thumb(product.image_url, 56)} alt=""
                  className="w-7 h-7 flex-shrink-0 rounded border border-slate-100 bg-white object-contain" />
              )}
              <div className="min-w-0">
                <p className="text-xs font-bold text-slate-800 truncate">{item.product_name || item.name}</p>
                <div className="flex flex-wrap items-center gap-1 mt-0.5">
                  {(item.variant_label || item.variant_value) && (
                    <Badge variant="ghost" className="text-[8px] h-3.5 px-1 bg-blue-50 text-blue-600 font-bold uppercase">
                      {item.variant_label || item.variant_value}
                    </Badge>
                  )}
                  {isUnmatched && (
                    <Badge variant="ghost" className="text-[8px] h-3.5 px-1 bg-red-100 text-red-700 font-bold uppercase">
                      Kein Produkt-Match
                    </Badge>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isUnmatched && (
                <div className="text-right">
                  <p className="text-[9px] text-slate-400 uppercase font-bold">Verf.</p>
                  <p className={cn('text-xs font-black', isLowStock ? 'text-red-600' : 'text-slate-600')}>
                    {product?.stock || 0}
                  </p>
                </div>
              )}
              {(isLowStock || isUnmatched) && <AlertCircle className="w-4 h-4 text-red-500 animate-pulse" />}
            </div>
          </div>
        );
      })}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="sm:hidden w-full min-h-[44px] text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg cursor-pointer transition-colors hover:bg-slate-100 flex items-center justify-center gap-1.5"
        >
          {expanded
            ? <>Weniger anzeigen <ChevronUp className="w-3.5 h-3.5" /></>
            : <>{hiddenCount} weitere Position(en) anzeigen <ChevronDown className="w-3.5 h-3.5" /></>}
        </button>
      )}
    </div>
  );
}

export default function WarehouseWarenausgang() {
  const queryClient = useQueryClient();
  // Kunden mit eigener Zustellung: Lager liefert selbst, kein DPD-Etikett
  const eigeneZustellung = useEigeneZustellung();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  // Kaynak filtresi: 'all' | 'agent' | 'ebay' | 'shopify'
  const [sourceFilter, setSourceFilter] = useState('all');

  // 1. Fetch Orders and Products
  const { data: orders = [], isLoading: ordersLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list(),
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list(),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  // Aktif tab: "neu" (kommissioniert edilecek), "kommissioniert"
  // (versandbereit yapılacak) veya "versand" (Abfahrt/Zustellung) —
  // Versand alanı tek ekranda konsolide edildi.
  const [activeTab, setActiveTab] = useState('neu');

  const matchesSearch = (o) =>
    (o.lead_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.agent_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (o.platform_order_id || '').toLowerCase().includes(searchTerm.toLowerCase());

  const matchesSource = (o) => {
    if (sourceFilter === 'all') return true;
    if (sourceFilter === 'agent') return !o.source || o.source === 'agent';
    return o.source === sourceFilter;
  };

  // Depoya YALNIZCA gerçek siparişler (Bestellung) düşer — istisnasız.
  // Angebot'lar (yeni veya eski, onaylı veya onaysız) burada listelenmez;
  // silinmezler, Admin → Aufträge altında durmaya devam ederler. Admin bir
  // Angebot'u onayladığında kayıt Bestellung'a dönüşür ve o zaman düşer.
  const isBestellung = isBestellungShared;

  const neuOrders = useMemo(() => {
    return orders
      .filter(isBestellung)
      .filter(o => o.status === 'Neu')
      .filter(matchesSearch)
      .filter(matchesSource)
      .sort((a, b) => (a.priority || 1) - (b.priority || 1)
        || new Date(b.created_date) - new Date(a.created_date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchTerm, sourceFilter]);

  const kommissioniertOrders = useMemo(() => {
    return orders
      .filter(isBestellung)
      .filter(o => o.status === 'Kommissioniert')
      .filter(matchesSearch)
      .filter(matchesSource)
      .sort((a, b) => new Date(b.picked_at || b.updated_date || b.created_date)
        - new Date(a.picked_at || a.updated_date || a.created_date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchTerm, sourceFilter]);

  // Versand sekmesi: sevke hazır + yolda olan siparişler birlikte
  const versandOrders = useMemo(() => {
    return orders
      .filter(isBestellung)
      .filter(o => o.status === 'Versandbereit' || o.status === 'Unterwegs')
      .filter(matchesSearch)
      .filter(matchesSource)
      .sort((a, b) => new Date(b.delivery_note_at || b.updated_date || b.created_date)
        - new Date(a.delivery_note_at || a.updated_date || a.created_date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchTerm, sourceFilter]);

  // Zugestellt sekmesi: teslim edilen + faturalanan + kapanan siparişler —
  // depo tarafı için salt-okunur arşiv (belgeler yeniden indirilebilir).
  const zugestelltOrders = useMemo(() => {
    return orders
      .filter(isBestellung)
      .filter(o => ['Geliefert', 'Berechnet', 'Abgeschlossen'].includes(o.status))
      .filter(matchesSearch)
      .filter(matchesSource)
      .sort((a, b) => new Date(b.delivered_at || b.updated_date || b.created_date)
        - new Date(a.delivered_at || a.updated_date || a.created_date));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchTerm, sourceFilter]);

  const TAB_STATUSES = {
    neu: ['Neu'],
    kommissioniert: ['Kommissioniert'],
    versand: ['Versandbereit', 'Unterwegs'],
    zugestellt: ['Geliefert', 'Berechnet', 'Abgeschlossen'],
  };

  const pendingOrders = activeTab === 'neu'
    ? neuOrders
    : activeTab === 'kommissioniert'
      ? kommissioniertOrders
      : activeTab === 'versand'
        ? versandOrders
        : zugestelltOrders;

  // Kaynak sayaçları — kaynak filtresi uygulanmadan, aktif tab bazında
  const tabOrders = useMemo(() => {
    const statuses = TAB_STATUSES[activeTab] || ['Neu'];
    return orders.filter(isBestellung).filter(o => statuses.includes(o.status)).filter(matchesSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, searchTerm, activeTab]);

  // ------------------------------------------------------------------
  // Aşama 1: Neu → Kommissioniert
  //   - commit_stock RPC (rezerv + stok eş zamanlı azalır, audit)
  //   - picking_number üretilir (KM-YYYY-NNNN)
  //   - orders.status = 'Kommissioniert' + picked_at + picked_by
  //   - Kommissionierschein PDF indirilir (lagerort sıralı)
  // ------------------------------------------------------------------
  const kommissioniereMutation = useMutation({
    mutationFn: async (order) => {
      const parseErrors = [];
      const positionen = parseArrayField(order.positionen ?? order.items, {
        fieldName: 'positionen',
        onError: (msg) => parseErrors.push(msg),
      });
      if (parseErrors.length > 0) {
        throw new Error('Bestellpositionen ungültig: ' + parseErrors.join(' '));
      }
      if (positionen.length === 0) {
        throw new Error('Keine Positionen in dieser Bestellung — Buchung abgebrochen.');
      }

      // 1) Doküman numaraları ÖNCE üretilir — üretilemezse stok düşülmeden
      //    işlem durur (numarasız belge oluşmasın). KM + LS birlikte:
      //    Kommissionierung anında irsaliye de kesilir (2 nüsha).
      let pickingNumber;
      try {
        pickingNumber = await generateDocNumber('KM');
      } catch (err) {
        throw new Error('Kommissionier-Nummer konnte nicht erzeugt werden: ' + err.message);
      }
      let noteNumber = order.delivery_note_number || null;
      if (!noteNumber) {
        try {
          noteNumber = await generateDocNumber('LS');
        } catch (err) {
          throw new Error('Liefer-Nummer konnte nicht erzeugt werden: ' + err.message);
        }
      }

      // 2) Stok düş (commit_stock)
      try {
        const result = await commitStock({
          orderId: order.id,
          actorEmail: user?.email,
        });
        if (result?.skipped) {
          toast.warning('Stok motoru devre dışı — hareket kaydedilmedi.');
        } else if (typeof result === 'number') {
          // SKU eşleşmeyen kalemler (product_id null) commit_stock'ta sessizce
          // atlanır — kullanıcıya görünür yap.
          const matched = positionen.filter((p) => p.product_id).length;
          const skippedLines = positionen.length - matched;
          if (skippedLines > 0) {
            toast.warning(
              `${skippedLines} Position(en) ohne Produktzuordnung — Bestand wurde dafür NICHT gebucht.`
            );
          }
        }
      } catch (err) {
        if (err instanceof StockApiError) {
          throw new Error(stockErrorToGerman(err));
        }
        throw err;
      }

      // PIM P4: Verfügbar-Bestand der betroffenen Produkte im Hintergrund
      // zu Shopify pushen (→ eBay folgt). Fehler blockieren den Lagerablauf nie.
      pushStockForProducts(positionen.map((p) => p.product_id)).catch(() => {});

      // PIM P5: FC-Bestellung als Shopify-Umsatz spiegeln (BYPASS-Bestand,
      // Tag fieldconnect-mirror — Sync importiert sie nicht zurück).
      // Noch nicht in Shopify (z. B. ältere Bestellung)? Jetzt nachsenden.
      // Server-Endpunkt ist idempotent: bereits gesendete bleiben unberührt.
      pushOrderInBackground(order.id, { orderNumber: order.order_number, silentSuccess: true });

      const now = new Date().toISOString();
      // 3) Order metadata güncelle (KM + LS numaraları birlikte)
      const updated = await base44.entities.Order.update(order.id, {
        status: 'Kommissioniert',
        picking_number: pickingNumber,
        picked_at: now,
        picked_by: user?.email || null,
        delivery_note_number: noteNumber,
        delivery_note_at: order.delivery_note_at || now,
        updated_date: now,
      });

      const docOrder = {
        ...order,
        ...updated,
        picking_number: pickingNumber,
        picked_at: now,
        picked_by: user?.email,
        delivery_note_number: noteNumber,
        delivery_note_at: order.delivery_note_at || now,
      };

      // 4) TEK çok sayfalı PDF: KM + Lieferschein (Lager) + Lieferschein (Kunde).
      //    Tek tek belgeler karttaki "Dokumente" düğmelerinden ayrıca inebilir.
      try {
        await downloadWarenausgangPaket(docOrder, products);
      } catch (err) {
        console.warn('[Warenausgang] Paket-PDF failed:', err);
        toast.warning('Paket-PDF konnte nicht erzeugt werden — über Dokumente erneut versuchen.');
      }

      // 5) Agent'ın açık "Lieferung" To-Do'su otomatik kapanır —
      //    Kommissioniert + Lieferschein üretildi (spesifikasyon).
      completeLieferungTasksForLead({
        leadId: order.lead_id,
        resultText: `Kommissioniert & Lieferschein ${noteNumber} erstellt`,
        actorEmail: user?.email,
      }).then((n) => {
        if (n > 0) queryClient.invalidateQueries(['agent-tasks']);
      }).catch(() => {});

      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['products']);
      toast.success('Kommissioniert — Paket-PDF (KM + 2× Lieferschein) erstellt.');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // ------------------------------------------------------------------
  // Aşama 2: Kommissioniert → Versandbereit
  //   - delivery_note_number üretilir (LS-YYYY-NNNN)
  //   - invoice_number üretilir (RE-YYYY-NNNN) — fatura malla birlikte gider
  //   - orders.status = 'Versandbereit' + delivery_note_at + invoiced_at
  //   - Lieferschein + Rechnung PDF'leri indirilir
  //
  // Fatura burada yalnızca ÜRETİLİR; muhasebe onayı (Berechnet aşaması)
  // admin akışında kalır. order.invoice_number zaten varsa (ör. admin
  // önceden fatura kesti) çifte numara üretilmez.
  // ------------------------------------------------------------------
  const versandbereitMutation = useMutation({
    mutationFn: async (order) => {
      // LS numarası normalde Kommissionierung adımında üretilir; eski
      // kayıtlar (LS'siz Kommissioniert) için burada telafi edilir.
      let noteNumber = order.delivery_note_number || null;
      const noteIsNew = !noteNumber;
      if (!noteNumber) {
        try {
          noteNumber = await generateDocNumber('LS');
        } catch (err) {
          throw new Error('Liefer-Nummer konnte nicht erzeugt werden: ' + err.message);
        }
      }

      // Rechnung festschreiben (Nummer + Buchung + offener Posten atomar in
      // der DB; idempotent). Scheitert das, wird der Versand NICHT blockiert —
      // die Rechnung erscheint dann als Entwurf in der Buchhaltung.
      let invoiceNumber = order.invoice_number || null;
      let invoiceData = null;
      try {
        const finalized = await ensureInvoice(order, user?.email);
        invoiceNumber = finalized.invoice_number || invoiceNumber;
        invoiceData = finalized._invoice || null;
      } catch (err) {
        console.warn('[Warenausgang] Rechnung failed:', err);
        toast.warning(`Rechnung konnte nicht erstellt werden (${err.message}) — bitte in der Buchhaltung erstellen.`);
      }
      const invoiceIsNew = false; // Nummer schreibt die DB-Funktion selbst

      const now = new Date().toISOString();
      const updated = await base44.entities.Order.update(order.id, {
        status: 'Versandbereit',
        delivery_note_number: noteNumber,
        delivery_note_at: order.delivery_note_at || now,
        ...(invoiceIsNew && {
          invoice_number: invoiceNumber,
          invoiced_at: now,
          invoiced_by: user?.email || null,
        }),
        updated_date: now,
      });

      const docOrder = {
        ...order,
        ...updated,
        delivery_note_number: noteNumber,
        delivery_note_at: order.delivery_note_at || now,
        ...(invoiceNumber && { invoice_number: invoiceNumber, invoiced_at: order.invoiced_at || now }),
        ...(invoiceData && { _invoice: invoiceData }),
      };

      // LS zaten Kommissionierung'da 2 nüsha indirildi; yalnızca eski
      // (LS'siz) kayıtlar için burada telafi indirmesi yapılır.
      if (noteIsNew) {
        try {
          await downloadLieferscheinBeideExemplare(docOrder, products);
        } catch (err) {
          console.warn('[Warenausgang] Lieferschein PDFs failed:', err);
          toast.warning('Lieferschein-PDFs konnten nicht erzeugt werden — über Dokumente erneut versuchen.');
        }
      }

      if (invoiceNumber) {
        try {
          await downloadRechnung(docOrder);
        } catch (err) {
          console.warn('[Warenausgang] Rechnung PDF failed:', err);
          toast.warning('Rechnung-PDF konnte nicht erzeugt werden — über Dokumente erneut versuchen.');
        }
      }

      return updated;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      toast.success('Rechnung erstellt — Auftrag ist versandbereit.');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  // ------------------------------------------------------------------
  // Versand aşamaları (tek ekran konsolidasyonu):
  //   Versandbereit → Unterwegs (Abfahrt) → Geliefert
  // ------------------------------------------------------------------
  const abfahrtMutation = useMutation({
    mutationFn: async (order) => {
      const now = new Date().toISOString();
      return base44.entities.Order.update(order.id, {
        status: 'Unterwegs',
        shipped_at: now,
        shipped_by: user?.email || null,
        updated_date: now,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      toast.success('Lieferung gestartet — Status: Unterwegs');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const geliefertMutation = useMutation({
    mutationFn: async (order) => {
      const now = new Date().toISOString();
      return base44.entities.Order.update(order.id, {
        status: 'Geliefert',
        delivered_at: now,
        delivered_by: user?.email || null,
        updated_date: now,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['orders']);
      toast.success('Lieferung bestätigt — Status: Geliefert');
    },
    onError: (err) => toast.error('Fehler: ' + err.message),
  });

  const getSourceIcon = (source) => {
    switch (source?.toLowerCase()) {
      case 'website': return <Globe className="w-4 h-4 text-blue-500" />;
      case 'ebay': return <ShoppingBag className="w-4 h-4 text-amber-500" />;
      case 'shopify': return <ShoppingBag className="w-4 h-4 text-emerald-500" />;
      default: return <User className="w-4 h-4 text-slate-500" />;
    }
  };

  const getSourceBadge = (source) => {
    const s = source?.toLowerCase() || 'agent';
    const configs = {
      agent: { label: 'Agent', class: 'bg-slate-100 text-slate-600 border-slate-200' },
      website: { label: 'Webseite', class: 'bg-blue-50 text-blue-600 border-blue-100' },
      ebay: { label: 'eBay', class: 'bg-amber-50 text-amber-600 border-amber-100' },
      shopify: { label: 'Shopify', class: 'bg-emerald-50 text-emerald-600 border-emerald-100' }
    };
    const cfg = configs[s] || configs.agent;
    return <Badge variant="outline" className={cn("text-[9px] uppercase font-bold px-1.5 h-4 border", cfg.class)}>{cfg.label}</Badge>;
  };

  const isLoading = ordersLoading || productsLoading;

  if (isLoading) {
    return (
      <div className="p-10 text-center text-slate-400">
        <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-red-500" />
        <p>Lade Ausgänge...</p>
      </div>
    );
  }

  return (
    <PageShell
      head={(
          <div className="flex items-center gap-2 mb-2 justify-between">
            <div className="flex items-center gap-2">
              <PageHeader icon={<ArrowUpFromLine className="w-5 h-5" />} title="Warenausgang" />
            </div>
            <div className="flex gap-2">
              <Badge variant="outline" className="bg-red-50 text-red-700 border-red-100 px-3 py-1">
                {pendingOrders.length} Aufträge
              </Badge>
            </div>
          </div>
      )}
    >

      {/* Phase Tabs — mobilde yatay kaydırılabilir, 44px dokunma hedefi */}
      <div
        className="flex gap-1 sm:gap-2 border-b border-slate-200 overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="Warenausgang Phasen"
      >
        {[
          { key: 'neu', label: 'Neu', count: neuOrders.length, hint: '→ Kommissionieren', active: 'border-red-600 text-red-700' },
          { key: 'kommissioniert', label: 'Kommissioniert', count: kommissioniertOrders.length, hint: '→ Versandbereit', active: 'border-purple-600 text-purple-700' },
          { key: 'versand', label: 'Versand', count: versandOrders.length, hint: '→ Zustellung', active: 'border-indigo-600 text-indigo-700' },
          { key: 'zugestellt', label: 'Zugestellt', count: zugestelltOrders.length, hint: 'Archiv', active: 'border-emerald-600 text-emerald-700' },
        ].map((tab) => (
          <button
            key={tab.key}
            role="tab"
            aria-selected={activeTab === tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={cn(
              'flex-shrink-0 min-h-[44px] px-3 sm:px-4 py-2 text-sm font-semibold border-b-2 -mb-[1px] transition-colors whitespace-nowrap cursor-pointer',
              activeTab === tab.key
                ? tab.active
                : 'border-transparent text-slate-500 hover:text-slate-800'
            )}
          >
            {tab.label}
            <span className={cn(
              'ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full text-[10px] font-bold',
              activeTab === tab.key ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-500'
            )}>
              {tab.count}
            </span>
            <span className="hidden lg:inline ml-2 text-[10px] font-normal text-slate-400">{tab.hint}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input 
          placeholder="Nach Kunde, Agent veya Platform-ID suchen..." 
          className="pl-10 h-11 border-slate-200 shadow-sm bg-white"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Stats Summary — tıklanabilir kaynak filtreleri (mobilde 4'lü kompakt) */}
      <div className="grid grid-cols-4 gap-1.5 sm:gap-3">
        {[
          { key: 'all', label: 'Alle', labelClass: 'text-slate-500', valueClass: 'text-slate-900', activeClass: 'border-slate-800 ring-1 ring-slate-800' },
          { key: 'agent', label: 'Agenten', labelClass: 'text-slate-400', valueClass: 'text-slate-900', activeClass: 'border-slate-800 ring-1 ring-slate-800' },
          { key: 'ebay', label: 'eBay', labelClass: 'text-amber-500', valueClass: 'text-amber-900', activeClass: 'border-amber-500 ring-1 ring-amber-500' },
          { key: 'shopify', label: 'Shopify', labelClass: 'text-emerald-500', valueClass: 'text-emerald-900', activeClass: 'border-emerald-500 ring-1 ring-emerald-500' },
        ].map((src) => {
          const count = src.key === 'all'
            ? tabOrders.length
            : src.key === 'agent'
              ? tabOrders.filter(o => !o.source || o.source === 'agent').length
              : tabOrders.filter(o => o.source === src.key).length;
          return (
            <Card
              key={src.key}
              onClick={() => setSourceFilter(src.key)}
              className={cn(
                'p-2.5 sm:p-4 min-h-[56px] bg-white border-slate-200 cursor-pointer transition-all hover:shadow-md select-none text-center sm:text-left',
                sourceFilter === src.key && src.activeClass,
              )}
            >
              <p className={cn('text-[9px] sm:text-[10px] font-bold uppercase truncate', src.labelClass)}>{src.label}</p>
              <p className={cn('text-lg sm:text-xl font-black', src.valueClass)}>{count}</p>
            </Card>
          );
        })}
      </div>

      {/* Orders List */}
      <div className="space-y-4">
        {pendingOrders.length === 0 ? (
          <Card className="border-2 border-dashed">
            <CardContent className="p-16 text-center text-slate-400">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-20 text-emerald-500" />
              <p className="font-medium text-slate-600">Keine offenen Ausgänge.</p>
              <p className="text-xs">Alle Bestellungen wurden erfolgreich bearbeitet.</p>
            </CardContent>
          </Card>
        ) : (
          pendingOrders.map((order) => {
            let positionen = order.positionen || [];
            if (typeof positionen === 'string') { try { positionen = JSON.parse(positionen); } catch { positionen = []; } }

            return (
              <Card key={order.id} className="overflow-hidden border-slate-200 hover:shadow-md transition-shadow">
                <div className="flex flex-col md:flex-row divide-y md:divide-y-0 md:divide-x">
                  {/* Left: Customer/Source Info */}
                  <div className="p-4 md:w-64 bg-slate-50/50 space-y-3">
                    <div className="flex items-center justify-between">
                      {getSourceBadge(order.source)}
                      {istEigeneZustellung(order, eigeneZustellung) && (
                        <Badge variant="outline" className="text-[9px] uppercase font-bold px-1.5 h-4 border bg-slate-800 text-white border-slate-800" title="Wir liefern selbst — nicht an Shopify/DPD">
                          Eigene Zustellung
                        </Badge>
                      )}
                      <p className="text-[10px] font-mono text-slate-400">#{order.platform_order_id || order.id.slice(0, 8)}</p>
                    </div>
                    <div>
                      <h3 className="font-black text-slate-900 leading-tight">{order.lead_name}</h3>
                      <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-1">
                        <Clock className="w-3 h-3" />
                        {new Date(order.created_date).toLocaleDateString()}
                      </p>
                    </div>
                    {order.agent_name && (
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <User className="w-3.5 h-3.5 text-slate-400" />
                        <span className="text-[10px] font-bold text-slate-600 uppercase">{order.agent_name}</span>
                      </div>
                    )}
                  </div>

                  {/* Middle: Items List */}
                  <div className="flex-1 p-4 space-y-3">
                    <div className="flex items-center gap-2 mb-2">
                      <Boxes className="w-4 h-4 text-slate-400" />
                      <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider">Positionen ({positionen.length})</span>
                    </div>
                    <PositionenList positionen={positionen} products={products} />
                  </div>

                  {/* Right: Actions */}
                  <div className="p-4 md:w-48 flex flex-col justify-center items-center gap-3 bg-slate-50/30">
                    <div className="text-center mb-1">
                      <p className="text-[10px] font-bold text-slate-400 uppercase">Status</p>
                      <StatusBadge status={order.status || 'Neu'} type="orderStatus" className="mt-1" />
                    </div>
                    {activeTab === 'neu' && (
                      <>
                        <Button
                          disabled={kommissioniereMutation.isPending}
                          className="w-full bg-purple-600 hover:bg-purple-700 text-white font-black uppercase text-[11px] h-11 shadow-lg shadow-purple-200"
                          onClick={() => kommissioniereMutation.mutate(order)}
                        >
                          {kommissioniereMutation.isPending
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <><CheckCircle2 className="w-4 h-4 mr-1.5" /> Kommissionieren</>}
                        </Button>
                        <p className="text-[8px] text-slate-400 text-center px-2">
                          Bestand wird gebucht — 1 PDF (3 Seiten): KM + Lieferschein Lager & Kunde.
                        </p>
                      </>
                    )}
                    {activeTab === 'kommissioniert' && (
                      <>
                        <Button
                          disabled={versandbereitMutation.isPending}
                          className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-[11px] h-11 shadow-lg shadow-indigo-200"
                          onClick={() => versandbereitMutation.mutate(order)}
                        >
                          {versandbereitMutation.isPending
                            ? <Loader2 className="w-4 h-4 animate-spin" />
                            : <><Truck className="w-4 h-4 mr-2" /> Versandbereit</>}
                        </Button>
                        <p className="text-[8px] text-slate-400 text-center px-2">
                          Rechnung wird erzeugt, Auftrag ist bereit zur Abfahrt.
                        </p>
                      </>
                    )}
                    {activeTab === 'versand' && (
                      <>
                        {order.status === 'Versandbereit' && (
                          <Button
                            disabled={abfahrtMutation.isPending}
                            className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black uppercase text-[11px] h-11 shadow-lg shadow-amber-200"
                            onClick={() => abfahrtMutation.mutate(order)}
                          >
                            {abfahrtMutation.isPending
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <><Truck className="w-4 h-4 mr-2" /> Abfahrt</>}
                          </Button>
                        )}
                        {order.status === 'Unterwegs' && (
                          <Button
                            disabled={geliefertMutation.isPending}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase text-[11px] h-11 shadow-lg shadow-emerald-200"
                            onClick={() => geliefertMutation.mutate(order)}
                          >
                            {geliefertMutation.isPending
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <><CheckCircle2 className="w-4 h-4 mr-2" /> Geliefert</>}
                          </Button>
                        )}
                        <p className="text-[8px] text-slate-400 text-center px-2">
                          {order.status === 'Versandbereit'
                            ? 'Fahrzeug beladen? Abfahrt startet die Zustellung.'
                            : 'Ware beim Kunden? Zustellung bestätigen.'}
                        </p>
                      </>
                    )}
                    {activeTab === 'zugestellt' && (
                      <p className="text-[9px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2 text-center">
                        {order.delivered_at
                          ? `Zugestellt am ${new Date(order.delivered_at).toLocaleDateString('de-DE')}`
                          : 'Zugestellt'}
                        {order.status !== 'Geliefert' ? ` · ${order.status}` : ''}
                      </p>
                    )}
                    <OrderDocuments order={order} products={products} />
                  </div>
                </div>
              </Card>
            );
          })
        )}
      </div>
    </PageShell>
  );
}
